import { useEffect, useMemo, useState } from "react";
import { Phone, Plus, Search, MessageCircle, Send, Mail, Calendar, FileText, X } from "lucide-react";
import api from "../lib/api";
import Modal from "./Modal";
import DateTimePicker from "./DateTimePicker";
import VoiceTextarea from "./VoiceTextarea";

const TYPES = [
  { v: "call", label: "Call", icon: Phone, color: "#10B981" },
  { v: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "#22C55E" },
  { v: "sms", label: "SMS", icon: Send, color: "#3B82F6" },
  { v: "email", label: "Email", icon: Mail, color: "#A78BFA" },
  { v: "meeting", label: "Meeting", icon: Calendar, color: "#F59E0B" },
  { v: "note", label: "Note", icon: FileText, color: "#647087" },
];

const empty = {
  customer_id: "",
  type: "call",
  direction: "out",
  duration_min: "",
  notes: "",
  linked_lead_id: "",
  linked_jobcard_id: "",
  linked_ticket_id: "",
  created_at_override: "",
};

/**
 * Log-call button + modal.
 *
 * Variants:
 *   "icon"     — icon-only (header). data-testid="quick-log-call-btn"
 *   "inline"   — full button used inside customer header / pages
 *   (default behaviour: open modal scoped to a preset customer if `customerId` prop is supplied)
 *
 * The previous floating gradient button is gone — the action now lives in
 * the global header (always one tap away) and on each customer card.
 */
export default function QuickLogCall({ onLogged, variant = "icon", customerId = null, leadId = null, label = "Log call" }) {
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [jobcards, setJobcards] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [c, l, j, t] = await Promise.all([
      api.get("/customers"),
      api.get("/leads").catch(() => ({ data: [] })),
      api.get("/jobcards").catch(() => ({ data: [] })),
      api.get("/tickets").catch(() => ({ data: [] })),
    ]);
    setCustomers(c.data);
    setLeads(l.data);
    setJobcards(j.data);
    setTickets(t.data);
  };

  useEffect(() => {
    if (open && customers.length === 0) load();
  }, [open]); // eslint-disable-line

  // Auto-select customer when scoped (per-customer button).
  useEffect(() => {
    if (open && customerId && form.customer_id !== customerId) {
      setForm((s) => ({ ...s, customer_id: customerId }));
    }
  }, [open, customerId]); // eslint-disable-line

  // Auto-link the call to the pre-selected lead when scoped per-lead.
  useEffect(() => {
    if (open && leadId && form.linked_lead_id !== leadId) {
      setForm((s) => ({ ...s, linked_lead_id: leadId }));
    }
  }, [open, leadId]); // eslint-disable-line

  const filteredCustomers = useMemo(() => {
    if (!q) return customers.slice(0, 12);
    const s = q.toLowerCase();
    return customers
      .filter(
        (c) =>
          c.name.toLowerCase().includes(s) ||
          (c.email || "").toLowerCase().includes(s) ||
          (c.phone || "").includes(q)
      )
      .slice(0, 20);
  }, [q, customers]);

  const customerLeads = leads.filter((l) => l.customer_id === form.customer_id);
  const customerJobs = jobcards.filter((j) => j.customer_id === form.customer_id);
  const customerTickets = tickets.filter((t) => t.customer_id === form.customer_id);

  const reset = () => {
    setForm(empty);
    setQ("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) return;
    setSaving(true);
    try {
      await api.post("/calls", {
        customer_id: form.customer_id,
        type: form.type,
        direction: form.direction,
        notes: form.notes,
        duration_min: form.duration_min ? Number(form.duration_min) : null,
        linked_lead_id: form.linked_lead_id || null,
        linked_jobcard_id: form.linked_jobcard_id || null,
        linked_ticket_id: form.linked_ticket_id || null,
        created_at_override: form.created_at_override || null,
      });
      reset();
      setOpen(false);
      onLogged?.();
    } finally {
      setSaving(false);
    }
  };

  const selectedCustomer = customers.find((c) => c.id === form.customer_id);

  return (
    <>
      {variant === "icon" ? (
        <button
          onClick={() => setOpen(true)}
          data-testid="quick-log-call-btn"
          className="p-1.5 rounded text-[#a0aabf] hover:text-[#1fb6c1] transition"
          title="Log a call / activity"
        >
          <Phone className="w-4 h-4" />
        </button>
      ) : variant === "row" ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={leadId ? `lead-row-calllog-${leadId}` : "quick-log-call-btn-row"}
          className="hv-btn hv-btn-ghost text-[10px] px-1.5 py-1"
          title="Log call / activity"
        >
          <Phone className="w-3 h-3 text-[#1fb6c1]" />
        </button>
      ) : (
        <button
          onClick={() => setOpen(true)}
          data-testid="quick-log-call-btn-inline"
          className="hv-btn hv-btn-ghost text-xs"
          title="Log a call / activity"
        >
          <Phone className="w-3.5 h-3.5" />
          {label}
        </button>
      )}

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          reset();
        }}
        title="Log activity"
        testId="quick-log-modal"
        maxWidth="max-w-2xl"
        footer={
          form.customer_id ? (
            <button
              type="button"
              onClick={submit}
              className="hv-btn hv-btn-primary w-full"
              data-testid="quick-log-submit"
              disabled={saving}
            >
              <Plus className="w-4 h-4" /> {saving ? "Saving…" : "Log activity"}
            </button>
          ) : null
        }
      >
        <form onSubmit={submit} className="space-y-4" data-testid="quick-log-form">
          {/* Customer picker */}
          {!form.customer_id ? (
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Customer *</label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#647087]" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search customer name, email, phone…"
                  className="hv-input hv-input-search"
                  data-testid="quick-log-search"
                />
              </div>
              <div className="mt-2 border border-white/10 rounded-md max-h-72 overflow-y-auto">
                {filteredCustomers.length === 0 && (
                  <div className="text-xs text-[#647087] p-3">No matching customer.</div>
                )}
                {filteredCustomers.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => setForm({ ...form, customer_id: c.id })}
                    data-testid={`quick-log-customer-${c.id}`}
                    className="w-full text-left px-3 py-2 border-b border-white/5 hover:bg-[#1fb6c1]/10 transition flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{c.name}</div>
                      <div className="text-xs text-[#647087] truncate">
                        {c.email || c.phone || "—"}
                      </div>
                    </div>
                    <span
                      className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                        c.status === "active"
                          ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
                          : "border-white/10 text-[#647087]"
                      }`}
                    >
                      {c.status || "active"}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between border border-[#1fb6c1]/30 bg-[#1fb6c1]/5 rounded-md p-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#1fb6c1]">Logging for</div>
                <div className="font-bold">{selectedCustomer?.name}</div>
              </div>
              <button
                type="button"
                onClick={() => setForm({ ...empty })}
                className="hv-btn hv-btn-ghost px-2 py-1 text-xs"
              >
                <X className="w-3 h-3" /> Change
              </button>
            </div>
          )}

          {form.customer_id && (
            <>
              {/* Type chips */}
              <div>
                <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Type</label>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-2">
                  {TYPES.map((t) => {
                    const active = form.type === t.v;
                    return (
                      <button
                        type="button"
                        key={t.v}
                        onClick={() => setForm({ ...form, type: t.v })}
                        data-testid={`quick-log-type-${t.v}`}
                        className="flex flex-col items-center gap-1 py-2 rounded-md border transition text-xs font-bold"
                        style={{
                          borderColor: active ? t.color : "rgba(255,255,255,0.1)",
                          background: active ? `${t.color}1a` : "transparent",
                          color: active ? t.color : "#a0aabf",
                        }}
                      >
                        <t.icon className="w-4 h-4" />
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Direction</label>
                  <select
                    value={form.direction}
                    onChange={(e) => setForm({ ...form, direction: e.target.value })}
                    className="hv-input mt-1"
                  >
                    <option value="out">Outbound</option>
                    <option value="in">Inbound</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Duration (min)</label>
                  <input
                    type="number"
                    value={form.duration_min}
                    onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
                    className="hv-input mt-1"
                    placeholder="optional"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Notes</label>
                <VoiceTextarea
                  value={form.notes}
                  onChange={(v) => setForm({ ...form, notes: v })}
                  placeholder="What was discussed? Decisions, next steps, full transcript… or tap the mic."
                  rows={8}
                  className="mt-1 leading-relaxed"
                  context="lead_activity"
                  testIdPrefix="quick-log-notes-voice"
                  data-testid="quick-log-notes"
                />
                <div className="text-[10px] text-[#647087] mt-1">
                  {form.notes.length} chars · re-open later to extend the conversation.
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
                  When (optional — leave blank for now)
                </label>
                <DateTimePicker
                  value={form.created_at_override}
                  onChange={(v) => setForm({ ...form, created_at_override: v })}
                  placeholder="Auto-stamps now if blank"
                  testId="quick-log-when"
                  className="mt-1"
                />
              </div>

              {(customerLeads.length > 0 || customerJobs.length > 0 || customerTickets.length > 0) && (
                <div className="grid sm:grid-cols-3 gap-3">
                  {customerLeads.length > 0 && (
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Link to lead</label>
                      <select
                        value={form.linked_lead_id}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            linked_lead_id: e.target.value,
                            linked_jobcard_id: "",
                            linked_ticket_id: "",
                          })
                        }
                        className="hv-input mt-1 text-xs"
                      >
                        <option value="">— None —</option>
                        {customerLeads.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {customerJobs.length > 0 && (
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Link to job</label>
                      <select
                        value={form.linked_jobcard_id}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            linked_jobcard_id: e.target.value,
                            linked_lead_id: "",
                            linked_ticket_id: "",
                          })
                        }
                        className="hv-input mt-1 text-xs"
                      >
                        <option value="">— None —</option>
                        {customerJobs.map((j) => (
                          <option key={j.id} value={j.id}>
                            {j.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {customerTickets.length > 0 && (
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Link to ticket</label>
                      <select
                        value={form.linked_ticket_id}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            linked_ticket_id: e.target.value,
                            linked_lead_id: "",
                            linked_jobcard_id: "",
                          })
                        }
                        className="hv-input mt-1 text-xs"
                      >
                        <option value="">— None —</option>
                        {customerTickets.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.subject}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

            </>
          )}
        </form>
      </Modal>
    </>
  );
}
